/**
 * Store Configuration
 *
 * Uses the abstraction layer internally while maintaining backward compatibility
 * for existing code that imports from this module.
 */

import {
  applicationSlice,
  attestationSlice,
  lookupSlice,
  missingItemFormSlice,
  missingItemSignatureSlice,
  missingItemSignedPdfSlice,
  sectionSlice,
  releaseSlice,
  reviewAndSubmitSlice,
  signatureSlice,
  userSlice,
  authorizationSlice,
  providerSlice,
} from './slices';

import { formSlice } from '@/forms/store';
import { authSlice } from '@/auth/store';
import { storeService } from './core/storeService';
import { reduxPersistStorageAdapter } from './core/adapters/reduxPersistStorageAdapter';
import type {
  IMiddleware,
  IReducer,
  IAction,
  ReducerEnhancer,
} from './core/types';
import type { IPersistor } from './core/types';

// Typed reducers map — used to infer RootState before casting
const typedReducers = {
  application: applicationSlice.reducer,
  section: sectionSlice.reducer,
  attestation: attestationSlice.reducer,
  auth: authSlice.reducer,
  form: formSlice.reducer,
  lookup: lookupSlice.reducer,
  missingItemForm: missingItemFormSlice.reducer,
  missingItemSignature: missingItemSignatureSlice.reducer,
  missingItemSignedPdf: missingItemSignedPdfSlice.reducer,
  release: releaseSlice.reducer,
  authorization: authorizationSlice.reducer,
  reviewAndSubmit: reviewAndSubmitSlice.reducer,
  signature: signatureSlice.reducer,
  user: userSlice.reducer,
  provider: providerSlice.reducer,
};

// RootState is derived from the typed reducers before any IReducer cast
export type RootState = {
  [K in keyof typeof typedReducers]: ReturnType<(typeof typedReducers)[K]>;
};

// Cast to the generic interface expected by the abstraction layer
const reducers: Record<string, IReducer> = typedReducers as unknown as Record<
  string,
  IReducer
>;

// Create reducer enhancer for logout state reset
// This wraps the final reducer to reset state on logout
const logoutReducerEnhancer: ReducerEnhancer = (baseReducer: IReducer) => {
  return (state: unknown, action: IAction) => {
    // Reset state to undefined on logout so reducers get initial state
    if (action.type === authSlice.actions.logout.toString()) {
      return baseReducer(undefined, action);
    }
    return baseReducer(state, action);
  };
};

// Persistor reference for logout middleware (Redux only passes getState/dispatch to
// middleware, so we capture the real store's persistor after creation).
// eslint-disable-next-line prefer-const -- assigned after createStore()
let logoutPersistor:
  | { flush(): Promise<void>; purge(): Promise<void> }
  | undefined;

// Adapt logout middleware to our interface (store arg not used; we use logoutPersistor)
const adaptedLogoutMiddleware: IMiddleware = () => next => async action => {
  const result = next(action);

  if (action.type === authSlice.actions.logout.toString()) {
    if (logoutPersistor) {
      await logoutPersistor.flush();
      await logoutPersistor.purge();
    }
    // Import AuthService dynamically to avoid circular dependencies
    const { AuthService } = await import('@/auth/services');
    new AuthService().destroy();
  }

  return result;
};

// Create store using abstraction
// Adapter will combine reducers, apply persistence, then wrap with logout enhancer
const storeInstance = storeService.createStore({
  reducer: reducers,
  reducerEnhancer: logoutReducerEnhancer,
  middleware: [adaptedLogoutMiddleware],
  persistConfig: {
    key: 'root',
    storage: reduxPersistStorageAdapter,
    blacklist: ['form'],
  },
});

logoutPersistor = storeInstance.persistor as typeof logoutPersistor;

export const store = storeInstance;

// Export persistor for backward compatibility
// Extract the underlying Redux persistor if available
const persistorWithRedux = store.persistor as
  | (IPersistor & { getReduxPersistor?: () => unknown })
  | undefined;
export const persistor = persistorWithRedux?.getReduxPersistor?.() ?? store.persistor;

// AppDispatch type - matches the dispatch function signature
export type AppDispatch = typeof store.dispatch;

export type AppStore = typeof store;

export const getStoreState = (): RootState => store.getState() as RootState;
